import cn from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";

import React, { useMemo } from "react";

import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";

import SitemapDialogBtn from "@/components/SitemapDialog";
import { AIRLINE_IMG, AIRLINE_NAME_MAP } from "@/constants/airline";
import { airlineBgColor } from "@/constants/config";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import { FlightItem } from "@/typings";
import { calculateTargetUrl } from "@/utils";

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s",
    // s: "seconds",
    // m: "1 minute",
    // mm: "%d minutes",
    s: "just now",
    m: "minutes ago",
    mm: "minutes ago",
    h: "1 hour ago",
    hh: "%d hours ago",
    d: "1 day ago",
    dd: "%d days ago",
    M: "1 month ago",
    MM: "%d months ago",
    y: "1 year ago",
    yy: "%d years ago",
  },
});

interface SummaryInfoProps {
  data: FlightItem;
  children?: React.ReactNode;
  showDetail: boolean;
  showLastSeen?: boolean;
  showSearchBtn?: boolean;
  onSearch?: (data: FlightItem) => void;
  showCabin?: boolean;
}

const SummaryInfo = ({
  data,
  children,
  showDetail,
  showLastSeen,
  showSearchBtn,
  showCabin = true,
  onSearch,
}: SummaryInfoProps) => {
  const theme = useThemeData();
  const lastSeen = useMemo(() => data?.ls, [data]);
  const products = useMemo(() => data?.fare?.products || [], [data]);
  const seats = useMemo(() => data?.ss, [data]);

  const { isMobile } = usePlatform();

  const stopInfo = useMemo(() => {
    return `${data.fare.travel_time_display.replace("h", " hr").replace("m", " min")}${` - `}${
      products.length === 1 ? "Nonstop" : `${products.length - 1} stop`
    }`;
  }, [data, products]);

  const airportInfo = useMemo(() => {
    return [products[0]?.origin, ...products.map((ele) => ele.destination)].join(" - ");
  }, [products]);

  const airline_img =
    data.airline_name.indexOf("&") !== -1 ? AIRLINE_IMG["MXA"] : AIRLINE_IMG[data.airline_code];
  const tooltip_text =
    data.airline_name.indexOf("&") !== -1 ? "Mixed Airline" : AIRLINE_NAME_MAP[data.airline_code];

  const buy_ticket_url = useMemo(() => {
    const products = data?.fare?.products || [];
    const origin_airport = products[0]?.origin;
    const arrival_airport = products[products.length - 1]?.destination;
    const departure_date = data?.date;
    const program_code = data?.program_code;
    const cabin = data?.payment?.cabin || data?.final_cabin_type;
    return calculateTargetUrl(
      program_code,
      origin_airport,
      arrival_airport,
      departure_date,
      cabin,
      true
    );
  }, [data]);

  // Check if last seen is less than 1 minute
  const isLastSeenLessThanMinute = useMemo(() => {
    return dayjs().unix() - lastSeen < 15;
  }, [lastSeen]);

  return (
    <Box className="flex items-center">
      <div
        className={cn("flex items-center justify-center pr-[20px] pl-[10px]", {
          "flex-col": showDetail,
        })}
      >
        <Tooltip placement="top" title={tooltip_text}>
          <Box
            className="rounded-[3px] p-[6px] flex items-center h-[50px]"
            sx={{
              backgroundColor: theme.isDarkMode ? airlineBgColor : "transparent",
              fontSize: 0,
            }}
          >
            <img src={airline_img} alt={data.airline_code} className="w-[38px] max-h-[38px]" />
          </Box>
        </Tooltip>

        {data.date ? (
          <Box
            className={cn("flex items-center justify-center flex-col w-[36px]", {
              "mt-[10px]": showDetail,
              "ml-[15px]": !showDetail,
            })}
          >
            <Box>
              <Typography fontSize={12} variant="body1">
                {dayjs(data.date).format("ddd")}
              </Typography>
            </Box>

            <Box
              className="px-[5px] rounded-[2px]"
              sx={{ backgroundColor: theme.isDarkMode ? "#ffffff29" : "#e1f5fe" }}
            >
              <Typography fontSize={13} variant="body1">
                {dayjs(data.date).format("M/D")}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </div>

      <div className="flex flex-col relative">
        <Typography variant="body1" color="textPrimary">
          {dayjs(products[0].departure_time).format("hh:mmA")}
          {` - `}
          {dayjs(products[products.length - 1].arrival_time).format("hh:mmA")}

          {data.fare.day_count > 0 ? (
            <Typography variant="caption" color="textSecondary" className="absolute pl-[4px]">
              +{data.fare.day_count}
            </Typography>
          ) : null}
        </Typography>

        <Typography variant="body2" color="textSecondary">
          {data.airline_name} {showCabin ? " ·  " : ""}
          {showCabin ? data.cabin_type : ""}
        </Typography>

        {/* <Typography variant="body2" color="textSecondary">
          
        </Typography> */}
       {showCabin && <Typography variant="body2" color="textSecondary">
          {seats == -1 || seats == undefined
            ? ""
            : (seats == 1 ? "Seat" : "Seats") +
              " Left: " +
              ` ${seats + (seats == 9 ? "+" : "")} `}{" "}
          {data.percent_premium
            ? (seats == -1 ? `` : " ·  ") + Math.round(data.percent_premium) + "% Premium"
            : ""}
        </Typography>}

        {showDetail ? (
          <>
            <Typography variant="body2" color="textSecondary">
              {stopInfo}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {airportInfo}
            </Typography>
            {data.cash_fare > 0 && (
              <div className="flex items-center">
                <Button
                  size="small"
                  sx={{ borderRadius: 2, paddingX: 1.5 }}
                  href={buy_ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MonetizationOnOutlinedIcon color="primary" sx={{ fontSize: 15 }} />
                  <Typography variant="body2" color="textSecondary" className="pl-[2px]">
                    Cash ${data.cash_fare.toLocaleString()}
                  </Typography>
                </Button>
              </div>
            )}
            {showLastSeen ? (
              <Typography variant="body2" color="textSecondary">
                Seen:{" "}
                {dayjs(lastSeen * 1000).fromNow() +
                  (seats == -1 ? "" : ` - ${seats} ` + (seats == 1 ? "seat" : "seats"))}
              </Typography>
            ) : (
              ""
            )}

            {showSearchBtn && !isLastSeenLessThanMinute ? (
              <Button
                component="label"
                variant="contained"
                color="primary"
                sx={{ borderRadius: "50px", marginTop: "4px", width: "160px" }}
                size="small"
                startIcon={<SearchIcon sx={{ fontSize: "18px" }} />}
                onClick={() => {
                  onSearch && onSearch(data);
                }}
              >
                Check Real-time
              </Button>
            ) : null}

            {data.isTop && (
              <Chip
                label="Panorama Flight Match"
                color="primary"
                size="small"
                icon={<StarBorderIcon color="primary" />}
              />
            )}
          </>
        ) : null}

        {isMobile && <SitemapDialogBtn data={data} />}
      </div>

      {children}
    </Box>
  );
};

export default React.memo(SummaryInfo);
